<template>
  <Box>
    <h2 class="text-t5 mb-3">Underhållsplan</h2>
    <div v-if="preambleText" v-html="preambleText"></div>
    <p>
      Rabatt/år innebär att du årligen får en rabatt eftersom du om du valt att inte beställa den
      typen av underhåll.
    </p>

    <div v-if="multipleContracts" class="mt-4">
      <h3 class="text-[18px] font-bold mt-0 md:mt-[30px] md:text-[20px] mb-2 max-w-[250px]">Välj kontakt</h3>
      <SelectField
        label=""
        class="md:max-w-md"
        placeholder="Välj kontrakt"
        :floatingLabel="false"
        :options="contracts"
        v-model="selectedContract"
        size="md"
        @update:modelValue="change"
      />
    </div>

    <div class="mt-8 space-y-8" v-if="contracts.length > 0">
      <template v-for="(roomData, room) in this.roomMeasures" :key="room">
        <!-- Desktop-table -->
        <table class="text-sm hidden w-full xl:table">
          <thead>
            <tr class="border-b border-b-gray-30">
              <th class="text-t5 w-[20%] px-4 text-left font-bold py-2">{{ room }}</th>
              <th
                class="text-b3 col-span-2 w-[30%] px-4 text-left font-bold uppercase tracking-wider"
              >
                <p class="text-label">Åtgärd</p>
              </th>
              <th class="text-b3 px-4 text-left font-bold uppercase tracking-wider">
                <p class="text-label">Utfört</p>
              </th>
              <th class="text-b3 px-4 text-left font-bold uppercase tracking-wider">
                <p class="text-label">Planerat</p>
              </th>
              <th class="text-b3 px-4 text-left font-bold uppercase tracking-wider">
                <p class="text-label">Kostnad</p>
              </th>
              <th class="text-b3 px-4 text-left font-bold uppercase tracking-wider">
                <p class="text-label">Rabatt</p>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in roomData"
              :key="item"
              class="border-b border-t border-solid border-gray-light odd:bg-gray-10 even:bg-white"
            >
             <td class="text-b1 w-[20%] px-4 py-2">
                <p class="font-bold">
                  {{ item.unit }}
                </p>
              </td>
              <td class="text-b2 w-[30%] px-4 py-2">
                <p>
                  {{ item.action }}
                </p>
              </td>
              <td class="text-b2 px-4 py-2">
                <p>
                  {{ item.completed }}
                </p>
              </td>
              <td class="text-b2 px-4 py-2">
                <p>
                  {{ item.planned }}
                </p>
              </td>
              <td class="text-b2 px-4 py-2">
                <p>
                  {{ item.cost }}
                </p>
              </td>
              <td class="text-b2 px-4 py-2">
                <p>
                  {{ item.discount || '-' }}
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </template>

      <!-- Mobile-stacked version -->
        <div class="xl:hidden">
          <div
            v-for="(item, index) in flattenedRoomMeasures"
            :key="item.id"
          >
            <div
              class="flex flex-col gap text-b2 p-4 rounded  border-gray-30 p-4"
              :class="index % 2 === 0 ? 'bg-gray-10 border-b border-t' : 'bg-white'"
            >
              <h3 class="mb-2 font-bold">{{ item.room }} – {{ item.unit }}</h3>
              <div class="flex gap-1">
                <p class="font-semibold mb-4 text-b2">Åtgärd:</p>
                <p class="text-b2">{{ item.action }}</p>
              </div>
              <div class="flex gap-8 justify-between">
                <div class="flex flex-col">
                    <p class="font-bold text-[11px] uppercase">Utfört</p>
                    <p>{{ item.completed }}</p>
                </div>
                <div class="flex flex-col">
                  <p class="font-bold text-[11px] uppercase">Planerat</p>
                  <p>{{ item.planned }}</p>
                </div>
                <div class="flex flex-col">
                  <p class="font-bold text-[11px] uppercase">Kostnad</p>
                  <p>{{ item.cost }} kr</p>
                </div>
                <div class="flex flex-col">
                  <p class="font-bold text-[11px] uppercase">Rabatt</p>
                  <p>{{ item.discount || '-' }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

      <!-- Totalsumma -->
      <div v-if="totalDiscount" class="text-sm mt-4 flex justify-end gap-4 px-4 text-right">
        <span class="font-medium text-turquoise-60">Din totala rabatt per år</span>
        <span class="font-semibold text-turquoise-60">{{ totalDiscount }} kr</span>
      </div>
    </div>

    <div class="mt-4 rounded-[15px] bg-danger-10 p-4 text-wine-red" v-if="showError || (contracts.length == 0 || measures.length == 0 && loaded == true)">
      <p>Underhållsplan kunde inte hittas</p>
    </div>
  </Box>
</template>

<script>
import Box from '@/components/ui/box';
import SelectField from '@/components/ui/form/select';


const roomMapping = {
  "Vardagsrum/kök": "Vardagsrum",
  "Hall 1": "Hall",
};

export default {
  name: 'MaintenancePlan',
  components: {
    Box,
    SelectField,
  },
  props: {
    preambleText: {
      type: String,
      default: '',
    },
    multipleContracts: {
      type: Boolean,
      default: false,
    },
    contracts: {
      type: Array,
      default: () => [],
    },
    showError: {
      type: Boolean,
      default: false,
    },
    loaded: {
      type: Boolean,
      default: false,
    },
    measures: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedContract: null,
      totalDiscount: 0,
      roomMeasures: null
    };
  },

  computed: {
    flattenedRoomMeasures() {
      const result = [];
      Object.entries(this.roomMeasures || {}).forEach(([room, items]) => {
        items.forEach((item) => {
          result.push({ ...item, room });
        });
      });
      return result;
    },
  },

  methods: {
    change(selectedContract) {
      this.$emit('update:modelValue', selectedContract);
    }
  },

  watch: {
    measures: {
      immediate: true,
      handler (measures, oldValue) {
        let discount = 0;

        const transformedData = measures.reduce((acc, item, index) => {
          const roomName = roomMapping[item.roomDescription] || item.roomDescription;

          if (!acc[roomName]) {
            acc[roomName] = [];
          }

          acc[roomName].push({
            id: index + 1,
            unit: item.unitDescription,
            action: item.measureText2.charAt(0).toUpperCase() + item.measureText2.slice(1), // Capitalize first letter
            completed: item.latestMeasureYear.toString(),
            planned: item.upcomingMeasureYear.toString(),
            cost: item.advancementCost.toFixed(0), // Round cost to integer
            discount: item.discount > 0 ? item.discount.toString() : null
          });

          discount += item.discount;

          return acc;
        }, {});

        this.totalDiscount = discount;
        this.roomMeasures = transformedData;
      },
    }
  },
};
</script>

